/**
 * Avisos de baja de una promoción (pedido de Alan del 16/10/2026): la baja ya no es obligatoria en las
 * plantillas de marketing, pero antes de mandar se avisa lo que falta. Son las mismas tres advertencias
 * que el validador del backend (`marketing_pie_baja`, `marketing_palabra_baja`, `marketing_boton_baja` en
 * `util/plantilla.util.ts`), dichas corto para el paso de confirmar una campaña.
 *
 * Quien responde BAJA, STOP o pide la baja con sus palabras queda dado de baja igual, con o sin el pie.
 */
import type { BajaCampana, BotonPlantilla } from "../types";
import { ID_BOTON_BAJA } from "./plantilla";

/** Lo que hace falta de una plantilla: sirve un borrador del editor o el detalle de una ya aprobada. */
export interface PlantillaConBaja { categoria: string; pie?: string; botones?: BotonPlantilla[] }

export function avisosDeBaja(p: PlantillaConBaja): string[] {
  if (p.categoria !== "MARKETING") return [];
  const out: string[] = [];
  const pie = (p.pie || "").trim();
  if (!pie) out.push("El mensaje no dice cómo darse de baja: la ley lo pide en toda publicidad (Decreto 1558/2001, art. 27).");
  // BAJA o STOP como palabra suelta, en mayúsculas o en minúsculas: como el validador (`marketing_palabra_baja`).
  else if (!/(^|[^\p{L}])(baja|stop)(?![\p{L}])/iu.test(pie)) out.push("El pie no dice BAJA ni STOP: si pide otra palabra, quien la escriba puede no quedar dado de baja.");
  if (!(p.botones || []).some((b) => b.tipo === "QUICK_REPLY" && b.id === ID_BOTON_BAJA)) {
    out.push("No tiene el botón de baja: darse de baja cuesta más y más gente bloquea o reporta el número, lo que le baja la calidad en Meta.");
  }
  return out;
}

/*
 * La baja de cada campaña (paso «Horario y baja», 26/10: «que pueda decidir si pone botón, texto o lo que quiera por
 * campaña»). La plantilla ya está aprobada por Meta: lo que se elige tiene que estar en ella. Espejo de
 * `util/baja-campana.util.ts` del backend, que es el que frena al confirmar.
 */

export type EleccionBaja = "boton" | "texto" | "los_dos" | "ninguna";

/** «BAJA» o «STOP» como palabra suelta, en mayúsculas o en minúsculas (como `marketing_palabra_baja`). */
const PALABRA_BAJA = /(^|[^\p{L}])(baja|stop)(?![\p{L}])/iu;

/** Qué formas de darse de baja trae la plantilla: el botón (el nuestro o uno que dice lo del botón de la marca) y BAJA o STOP en el pie o el cuerpo. */
export function bajaDePlantilla(p: { botones?: BotonPlantilla[] | null; pie?: string | null; cuerpo?: string | null } | null | undefined, botonBaja?: string | null): BajaCampana {
  const titulo = String(botonBaja || "").trim().toLowerCase();
  const boton = (p?.botones || []).some(
    (b) => b?.tipo === "QUICK_REPLY" && (String(b.id || "").trim() === ID_BOTON_BAJA || (!!titulo && String(b.titulo || "").trim().toLowerCase() === titulo)),
  );
  return { boton, texto: PALABRA_BAJA.test(String(p?.pie || "")) || PALABRA_BAJA.test(String(p?.cuerpo || "")) };
}

export const eleccionDeBaja = (b: BajaCampana | null | undefined): EleccionBaja | "" =>
  !b ? "" : b.boton && b.texto ? "los_dos" : b.boton ? "boton" : b.texto ? "texto" : "ninguna";

export const bajaDeEleccion = (e: EleccionBaja): BajaCampana => ({ boton: e === "boton" || e === "los_dos", texto: e === "texto" || e === "los_dos" });

/** Por qué una opción no se puede elegir con esta plantilla ("" = se puede). */
export function motivoBajaNoDisponible(e: EleccionBaja, trae: BajaCampana, botonBaja?: string | null): string {
  const quiere = bajaDeEleccion(e);
  const boton = `«${String(botonBaja || "").trim() || "No quiero más"}»`;
  if (quiere.boton && quiere.texto && !trae.boton && !trae.texto) return `La plantilla no tiene el botón ${boton} ni dice BAJA.`;
  if (quiere.boton && !trae.boton) return `La plantilla no tiene el botón ${boton}.`;
  if (quiere.texto && !trae.texto) return "La plantilla no dice BAJA (ni STOP) en el pie ni en el texto.";
  return "";
}
